import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import HeroComponent from '../components/HeroComponent';
import { ConnectUs } from '../sections/ConnectUs';
import { SIPCalculator } from '../components/calculators/SIPCalculator';
import {
  Chart,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { PiChartLineUp } from 'react-icons/pi';
import { BiSolidReport } from 'react-icons/bi';
import { BsFillHouseFill } from 'react-icons/bs';
import { GiMoneyStack, GiPayMoney } from 'react-icons/gi';
import { FiChevronDown } from 'react-icons/fi';
import { LumpsumCalculator } from '../components/calculators/LumpsumCalculator';
import { SWPCalculator } from '../components/calculators/SWPCalculator';
import { InflationCalculator } from '../components/calculators/InflationCalculator';
import { RetirementCalculator } from '../components/calculators/RetirementCalculator';
import backgroundImage from '../assets/HomeBackground.png';
import { contactInfo } from '../config/navigation';
import { Heading } from '../components/typography/Heading';
import { SubHeading } from '../components/typography/SubHeading';

Chart.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, Title, Tooltip, Legend);

const Container = styled.div`
  padding: 20px;
  max-width: 1200px;
  margin: 0 auto;
`;

const Navigation = styled.div`
  display: flex;
  margin-bottom: 52px;
  border-radius: 12px;
  flex-wrap: wrap;
  justify-content: center;
  background: #e8ebf3;
  padding: 4px;
  gap: 2px;

  @media (max-width: 768px) {
    display: none;
  }
`;

const MobileNavigation = styled.div`
  display: none;
  position: relative;
  margin-bottom: 52px;

  @media (max-width: 768px) {
    display: block;
  }
`;

const DropdownButton = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background-color: white;
  border-radius: 8px;
  color: black;
  cursor: pointer;
  font-weight: 500;
  font-size: 16px;
  border-color: #e2e8f0;
  border-width: 1px;
`;

const DropdownMenu = styled.div<{ isOpen: boolean }>`
  display: ${(props) => (props.isOpen ? 'block' : 'none')};
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  margin-top: 4px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 10;
`;

const DropdownItem = styled.button<{ active?: boolean }>`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  border: none;
  background: ${(props) => (props.active ? '#e8ebf3' : 'white')};
  color: #1e2a47;
  cursor: pointer;
  font-weight: ${(props) => (props.active ? '600' : '500')};
  text-align: left;

  &:first-child {
    border-radius: 8px 8px 0 0;
  }

  &:last-child {
    border-radius: 0 0 8px 8px;
  }

  &:hover {
    background: #e8ebf3;
  }
`;

const NavButton = styled.button<{ active?: boolean }>`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  border: none;
  border-radius: 8px;
  background: ${(props) => (props.active ? '#1E2A47' : 'transparent')};
  color: ${(props) => (props.active ? 'white' : '#1E2A47')};
  cursor: pointer;
  flex: 1;
  justify-content: center;
  font-weight: 500;
  transition: all 0.2s ease;
  min-width: 0;

  &:hover {
    background: ${(props) => (props.active ? '#1E2A47' : 'rgba(255, 255, 255, 0.5)')};
  }
`;

const CalculatorContainer = styled.div`
  margin: 0 auto;
  padding: 24px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

interface CalculatorProps {}

export const Calculators: React.FC<CalculatorProps> = () => {
  const [activeCalculator, setActiveCalculator] = useState('sip');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const calculators = [
    { id: 'sip', label: 'SIP Calculator', icon: PiChartLineUp },
    { id: 'lumpsum', label: 'Lumpsum Calculator', icon: BiSolidReport },
    { id: 'swf', label: 'SWP Calculator', icon: GiMoneyStack },
    { id: 'inflation', label: 'Inflation Calculator', icon: GiPayMoney },
    { id: 'retirement', label: 'Retirement Calculator', icon: BsFillHouseFill },
  ];

  const activeCalc = calculators.find((c) => c.id === activeCalculator);
  const ActiveIcon = activeCalc?.icon;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleCalculatorChange = (id: string) => {
    setActiveCalculator(id);
    setIsDropdownOpen(false);
  };

  return (
    <>
      <HeroComponent
        backgroundImage={backgroundImage}
        title="Financial Calculators"
        subtitle="Plan your investments with our comprehensive calculator suite"
        primaryButton={{
          link: `https://wa.me/${contactInfo.phoneNumber}`,
          text: 'Get Expert Advice',
        }}
        secondaryButton={{ link: '/about', text: 'Learn More' }}
        compact
      />
      <Container>
        <SubHeading>Calculator</SubHeading>
        <Heading>We&apos;re your partners in a shared vision.</Heading>
        <MobileNavigation ref={dropdownRef}>
          <DropdownButton onClick={() => setIsDropdownOpen(!isDropdownOpen)}>
            <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              {ActiveIcon && <ActiveIcon size={20} />}
              {activeCalc?.label || 'Select Calculator'}
            </span>
            <FiChevronDown
              size={20}
              style={{ transform: isDropdownOpen ? 'rotate(180deg)' : 'rotate(0)', transition: 'transform 0.2s' }}
            />
          </DropdownButton>
          <DropdownMenu isOpen={isDropdownOpen}>
            {calculators.map((calc) => {
              const Icon = calc.icon;
              return (
                <DropdownItem
                  key={calc.id}
                  active={activeCalculator === calc.id}
                  onClick={() => handleCalculatorChange(calc.id)}
                >
                  <Icon size={20} /> {calc.label}
                </DropdownItem>
              );
            })}
          </DropdownMenu>
        </MobileNavigation>
        <Navigation>
          <NavButton active={activeCalculator === 'sip'} onClick={() => setActiveCalculator('sip')}>
            <PiChartLineUp size={20} /> SIP Calculator
          </NavButton>
          <NavButton active={activeCalculator === 'lumpsum'} onClick={() => setActiveCalculator('lumpsum')}>
            <BiSolidReport size={20} /> Lumpsum Calculator
          </NavButton>
          <NavButton active={activeCalculator === 'swf'} onClick={() => setActiveCalculator('swf')}>
            <GiMoneyStack size={20} /> SWP Calculator
          </NavButton>
          <NavButton active={activeCalculator === 'inflation'} onClick={() => setActiveCalculator('inflation')}>
            <GiPayMoney size={20} /> Inflation Calculator
          </NavButton>
          <NavButton active={activeCalculator === 'retirement'} onClick={() => setActiveCalculator('retirement')}>
            <BsFillHouseFill size={20} /> Retirement Calculator
          </NavButton>
        </Navigation>

        <CalculatorContainer>
          {activeCalculator === 'sip' && <SIPCalculator />}
          {activeCalculator === 'lumpsum' && <LumpsumCalculator />}
          {activeCalculator === 'swf' && <SWPCalculator />}
          {activeCalculator === 'inflation' && <InflationCalculator />}
          {activeCalculator === 'retirement' && <RetirementCalculator />}
        </CalculatorContainer>
      </Container>
      <ConnectUs />
    </>
  );
};
